import React, { useRef } from 'react'
import styled from 'styled-components'
import { space, SpaceProps, layout, LayoutProps } from 'styled-system'

import { Text } from 'components'

type CardProps = SpaceProps &
  LayoutProps & {
    actionItems?: React.ReactNode
    body?: string | React.ReactNode
    title?: string | React.ReactNode
    isActive?: boolean
    isClickable?: boolean
    onClick?: () => void
    display?: string
  }

const Card: React.FC<CardProps> = ({
  actionItems,
  body,
  children,
  isActive = false,
  isClickable = false,
  onClick,
  title,
  display,
  ...cssProps
}) => {
  const parentRef = useRef<HTMLDivElement>(null)

  return (
    <Container isActive={isActive} isClickable={isClickable} onClick={onClick} ref={parentRef} {...cssProps}>
      {(title || actionItems) && (
        <CardHead>
          <Text lineHeight="24px" color={isActive ? 'gray7' : 'gray5'} fontWeight="bold" fontSize={16} marginTop="6px">
            {title}
          </Text>
          <ActionItems>{actionItems}</ActionItems>
        </CardHead>
      )}
      <CardBody display={display}>{body || children}</CardBody>
    </Container>
  )
}

export default Card

const Container = styled.div<SpaceProps & { isActive: boolean; isClickable: boolean }>`
  padding: 1rem;
  ${space}
  ${layout}
  background-color: ${(p) => p.theme.colors.gray1};
  border: 1px solid ${(p) => (p.isActive ? p.theme.colors.blue2 : p.theme.colors.defaultBorder)};
  border-radius: 4px;
  cursor: ${(p) => (p.isClickable ? 'pointer' : 'default')};
  user-select: none;
`

const CardHead = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 6px;
  min-height: 28px;
`

const ActionItems = styled.div`
  display: flex;

  & > div {
    cursor: pointer;
    position: relative;

    & > svg {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
`

const CardBody = styled.div<{ display?: string }>`
  display: ${(p) => p.display || 'flex'};
  width: 100%;
  justify-content: space-between;
  gap: 0.2rem;
  align-items: center;
`
